import { useEffect, useState } from "react";
import {
    kStageFirstReturnAd,
    kStageFirstReturnDeuce,
    kStageFirstServeAd,
    kStageFirstServeDeuce,
    kStageRally,
    kStageSecondReturnAd,
    kStageSecondReturnDeuce,
    kStageSecondServeAd,
    kStageSecondServeDeuce,
} from "../../utils/pstsDefines";

// Options for the "Next Shot" selector, in coding order.
const NEXT_SHOT_OPTIONS = [
  { value: kStageFirstServeDeuce, label: "First Serve Deuce" },
  { value: kStageSecondServeDeuce, label: "Second Serve Deuce" },
  { value: kStageFirstServeAd, label: "First Serve Ad" },
  { value: kStageSecondServeAd, label: "Second Serve Ad" },
  { value: kStageFirstReturnDeuce, label: "First Return Deuce" },
  { value: kStageSecondReturnDeuce, label: "Second Return Deuce" },
  { value: kStageFirstReturnAd, label: "First Return Ad" },
  { value: kStageSecondReturnAd, label: "Second Return Ad" },
  { value: kStageRally, label: "Rally" },
];

function CurrentMatchState({ match, activeSet, update, onSaveMatchState }) {
  // The set being actively coded isn't always the last one in match.sets
  // (e.g. later sets can already exist while going back to correct an
  // earlier one) — always prefer the caller-supplied active set over
  // guessing from the array, or this modal silently edits the wrong set.
  const [currentSet, setCurrentSet] = useState(
    activeSet ?? (match?.sets?.length > 0 ? match.sets[match.sets.length - 1] : null)
  );

  const [formData, setFormData] = useState({
    player1SetScore: currentSet?.scores?.player1SetScore ?? 0,
    player2SetScore: currentSet?.scores?.player2SetScore ?? 0,
    player1GameScore: currentSet?.scores?.player1GameScore ?? 0,
    player2GameScore: currentSet?.scores?.player2GameScore ?? 0,
    isPlayer1Serving:
      currentSet?.currentState?.currentServer?.guid === match?.player1?.guid,
    isPlayer2Serving:
      currentSet?.currentState?.currentServer?.guid === match?.player2?.guid,
    nextShot:
      currentSet?.currentState?.currentStage ?? kStageFirstServeDeuce,
    isTiebreak: currentSet?.isTiebreak ?? false,
    tiebreakAfter: match?.tiebreakAfter ?? 12,
    tiebreakLastSet: match?.tiebreakLastSet ?? false,
    tiebreakTo: match?.tiebreakTo ?? 7,
    tiebreakMargin: match?.tiebreakMargin ?? 2,
    // No-Ad scoring: at deuce (40-40) the next point wins the game outright
    // instead of requiring a two-point margin.
    noAd: (match?.scoreModeAdvantage ?? 0) === 1,
  });
  const {
    player1SetScore,
    player2SetScore,
    player1GameScore,
    player2GameScore,
    isPlayer1Serving,
    isPlayer2Serving,
    nextShot,
    isTiebreak,
    tiebreakAfter,
    tiebreakLastSet,
    tiebreakTo,
    tiebreakMargin,
    noAd,
  } = formData;

  const gameScores = ["00", "15", "30", "40", "A"];

  const onSubmit = () => {
    currentSet.scores = {
      player1SetScore: parseInt(player1SetScore),
      player2SetScore: parseInt(player2SetScore),
      player1GameScore: player1GameScore,
      player2GameScore: player2GameScore,
    };
    currentSet.currentState.scores = { ...currentSet.scores };
    currentSet.currentState.currentPlayer = isPlayer1Serving
      ? match.player1
      : match.player2;
    currentSet.currentState.currentServer = isPlayer1Serving
      ? match.player1
      : match.player2;
    currentSet.currentState.currentStage = parseInt(nextShot);
    currentSet.isTiebreak = isTiebreak;
    match.tiebreakTo = tiebreakTo ? parseInt(tiebreakTo) : 7;
    match.tiebreakMargin = tiebreakMargin ? parseInt(tiebreakMargin) : 2;
    match.tiebreakAfter = tiebreakAfter ? parseInt(tiebreakAfter) : 12;
    match.tiebreakLastSet = !tiebreakLastSet ? false : tiebreakLastSet;
    match.scoreModeAdvantage = noAd ? 1 : 0;
    onSaveMatchState();
  };

  const onMutate = (e) => {
    if (!e.target.files) {
      let val = e.target.value;
      if (
        (isTiebreak === false && e.target.id === "player1GameScore") ||
        e.target.id === "player2GameScore"
      ) {
        val = gameScores.indexOf(val);
      }
      setFormData((prevState) => ({
        ...prevState,
        [e.target.id]: val,
      }));
    }
  };

  const toggleTiebreak = () => {
    setFormData((prevState) => ({
      ...prevState,
      isTiebreak: !isTiebreak,
    }));
  };

  const toggleTiebreakLastSet = () => {
    setFormData((prevState) => ({
      ...prevState,
      tiebreakLastSet: !tiebreakLastSet,
    }));
  };

  const toggleNoAd = () => {
    setFormData((prevState) => ({
      ...prevState,
      noAd: !noAd,
    }));
  };

  const doGameScores = (cset) => {
    if (
      cset.isTiebreak !== true &&
      cset.scores.player1GameScore >= 4 &&
      cset.scores.player2GameScore >= 4
    ) {
      if (cset.scores.player1GameScore === cset.scores.player2GameScore) {
        setFormData((prevState) => ({
          ...prevState,
          player1GameScore: 3,
          player2GameScore: 3,
        }));
      } else if (cset.scores.player1GameScore > cset.scores.player2GameScore) {
        setFormData((prevState) => ({
          ...prevState,
          player1GameScore: 4,
          player2GameScore: 3,
        }));
      } else {
        setFormData((prevState) => ({
          ...prevState,
          player1GameScore: 3,
          player2GameScore: 4,
        }));
      }
    }
  };

  useEffect(() => {
    if (match && match.sets && match.sets.length > 0) {
      const cset = activeSet ?? match.sets[match.sets.length - 1];
      setCurrentSet(cset);
      setFormData({
        player1SetScore: cset.scores?.player1SetScore ?? 0,
        player2SetScore: cset.scores?.player2SetScore ?? 0,
        player1GameScore: cset.scores?.player1GameScore ?? 0,
        player2GameScore: cset.scores?.player2GameScore ?? 0,
        isPlayer1Serving:
          cset.currentState?.currentServer?.guid === match?.player1?.guid,
        isPlayer2Serving:
          cset.currentState?.currentServer?.guid === match?.player2?.guid,
        nextShot:
          cset.currentState?.currentStage ?? kStageFirstServeDeuce,
        isTiebreak: cset.isTiebreak ?? false,
        tiebreakTo: match?.tiebreakTo ?? 7,
        tiebreakMargin: match?.tiebreakMargin ?? 2,
        noAd: (match?.scoreModeAdvantage ?? 0) === 1,
      });
      doGameScores(cset);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [update, activeSet]);
  if (!match || !match.player1 || !match.player2) return null;

  const p1 = `${match.player1.firstName} ${match.player1.lastName.toUpperCase()}`;
  const p2 = `${match.player2.firstName} ${match.player2.lastName.toUpperCase()}`;

  const inputCls = "input input-bordered input-sm w-full";
  const selectCls = "select select-bordered select-sm w-full";
  const checkCls = "checkbox checkbox-sm";
  const labelCls = "text-sm font-medium text-base-content/80";
  const sectionCls = "text-xs font-semibold text-base-content/50 uppercase tracking-wide pt-2 pb-1 border-b border-base-300 mb-2";

  return (
    <div className="flex flex-col gap-4 text-sm">

      {/* ── Current Score ── */}
      <div>
        <div className={sectionCls}>Current Score</div>
        <div className="grid grid-cols-3 gap-x-3 gap-y-2 items-center">
          {/* Header row */}
          <div />
          <div className={`${labelCls} text-center truncate`} title={p1}>{p1}</div>
          <div className={`${labelCls} text-center truncate`} title={p2}>{p2}</div>

          {/* Server */}
          <div className={labelCls}>Server</div>
          <div className="flex justify-center">
            <input
              type="checkbox"
              checked={isPlayer1Serving}
              className={checkCls}
              onChange={() => setFormData((prev) => ({ ...prev, isPlayer1Serving: true, isPlayer2Serving: false }))}
            />
          </div>
          <div className="flex justify-center">
            <input
              type="checkbox"
              checked={isPlayer2Serving}
              className={checkCls}
              onChange={() => setFormData((prev) => ({ ...prev, isPlayer2Serving: true, isPlayer1Serving: false }))}
            />
          </div>

          {/* Set Score */}
          <div className={labelCls}>Set Score</div>
          <input id="player1SetScore" type="number" min="0" value={player1SetScore} onChange={onMutate} className={inputCls} />
          <input id="player2SetScore" type="number" min="0" value={player2SetScore} onChange={onMutate} className={inputCls} />

          {/* Tiebreak toggle */}
          <div className={labelCls}>Tiebreak</div>
          <div className="flex justify-center">
            <input type="checkbox" checked={isTiebreak} className={checkCls} onChange={toggleTiebreak} />
          </div>
          <div />

          {/* Game Score */}
          <div className={labelCls}>Game Score</div>
          {isTiebreak ? (
            <>
              <input id="player1GameScore" type="number" min="0" value={player1GameScore} onChange={onMutate} className={inputCls} />
              <input id="player2GameScore" type="number" min="0" value={player2GameScore} onChange={onMutate} className={inputCls} />
            </>
          ) : (
            <>
              <select id="player1GameScore" value={gameScores[player1GameScore]} onChange={onMutate} className={selectCls}>
                {gameScores.map((s) => <option key={s}>{s}</option>)}
              </select>
              <select id="player2GameScore" value={gameScores[player2GameScore]} onChange={onMutate} className={selectCls}>
                {gameScores.map((s) => <option key={s}>{s}</option>)}
              </select>
            </>
          )}
        </div>
      </div>

      {/* ── Next Shot ── */}
      <div>
        <div className={sectionCls}>Next Shot</div>
        <select
          id="nextShot"
          value={nextShot}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, nextShot: parseInt(e.target.value) }))
          }
          className={selectCls}
        >
          {NEXT_SHOT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {/* ── Match Style ── */}
      <div>
        <div className={sectionCls}>Match Style</div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={noAd}
            className={checkCls}
            onChange={toggleNoAd}
          />
          <span className="text-sm">
            No-Ad (deciding point at deuce, no advantage)
          </span>
        </label>
      </div>

      {/* ── Tiebreak Rules ── */}
      <div>
        <div className={sectionCls}>Tiebreak Rules</div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 items-center">
          <div className={labelCls}>After game #</div>
          <input id="tiebreakAfter" type="number" min="0" value={tiebreakAfter} onChange={onMutate} className={inputCls} />

          <div className={labelCls}>Last set tiebreak</div>
          <div className="flex items-center">
            <input id="tiebreakLastSet" type="checkbox" checked={tiebreakLastSet} className={checkCls} onChange={toggleTiebreakLastSet} />
          </div>

          <div className={labelCls}>Points to win</div>
          <input id="tiebreakTo" type="number" min="0" value={tiebreakTo} onChange={onMutate} className={inputCls} />

          <div className={labelCls}>Win margin</div>
          <input id="tiebreakMargin" type="number" min="0" value={tiebreakMargin} onChange={onMutate} className={inputCls} />
        </div>
      </div>

      {/* ── Save ── */}
      <div className="flex justify-end pt-2">
        <button className="btn btn-primary btn-sm" onClick={onSubmit}>Save</button>
      </div>
    </div>
  );
}

export default CurrentMatchState;
